import { useLocalStorage } from '@blackbird/ui-base';
import { ArrowDropDown, Refresh } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  LinearProgress,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import { bindPopover, bindToggle, usePopupState } from 'material-ui-popup-state/hooks';
import { useRouter } from 'next/router';
import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { TimepickerContext } from '@/contexts/timepicker';
import { ClockContext } from '@/hocs/with-clock';

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '80px',
    height: '40px',
    overflow: 'hidden',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '4px',
    padding: 0,
  },
  buttonContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexWrap: 'nowrap',
  },
  button: {
    height: '100%',
    minHeight: '0px',
    minWidth: '0px',
    width: '100%',
    borderRadius: '0px',
  },
  refreshButtonContainer: {
    width: '100%',
  },
  dropDownButton: {
    borderLeft: '1px solid rgba(0, 0, 0, 0.23)',
    height: '100%',
    minHeight: '0px',
    borderRadius: '0px',
    minWidth: '25px',
    padding: '0 0 3px 0',
  },
  dropDownArrow: {
    paddingTop: '0px',
  },
  progress: {
    marginTop: '-4px',
  },
  radio: {
    padding: '0',
    marginRight: theme.spacing(),
    '& svg': {
      width: '20px',
      height: '20px',
    },
  },
}));

interface Properties {
  loading: boolean;
  overrideStyle?: { [key: string]: string | number };
  pollIntervals?: number[];
  initialPollInterval?: number;
  onRefresh?(): void;
}

const POLL_INTERVALS = [(10).seconds, (30).seconds, (1).minutes, (5).minutes, (10).minutes];
const AUTOMATIC_RELOAD_MAX_SPAN_HOURS = 48;

const [, defaultPollInterval] = POLL_INTERVALS;

const LiveDataButton: FunctionComponent<Properties> = ({
  loading,
  overrideStyle,
  onRefresh,
  pollIntervals = POLL_INTERVALS,
  initialPollInterval = defaultPollInterval,
}) => {
  const { classes } = useStyles();
  const [t] = useTranslation();
  const router = useRouter();

  const [pollInterval, setPollInterval] = useLocalStorage<number | 'manual'>('pollInterval', initialPollInterval);

  const { setTime, from, to, relativeTimeInterval } = useContext(TimepickerContext);
  const { currentTime, start } = useContext(ClockContext);

  const requestedDuration = (to || currentTime).getTime() - from.getTime();

  // Disable live reloading if the requested duration is too long or the time range is in the past.
  const disableLiveReloading =
    AUTOMATIC_RELOAD_MAX_SPAN_HOURS < Math.floor(requestedDuration / (1).hours) || !!(to && to < currentTime);

  const lowestLiveInterval = Math.min(...pollIntervals);

  const refresh = useCallback(
    (newFrom: Date, newTo: Date | null) => {
      setTime(newFrom, newTo);

      onRefresh?.();
    },
    [from, to, onRefresh, setTime],
  );

  const currentTimeMillis = currentTime.getTime();

  const [baseTime, setBaseTime] = useState(currentTimeMillis);

  useEffect(() => start(), []);

  useEffect(() => {
    if (disableLiveReloading || !pollInterval || pollInterval === 'manual') {
      return;
    }

    const interval = setTimeout(() => {
      // If we are in relative time mode, update from
      const newFrom = relativeTimeInterval ? new Date(Date.now() - relativeTimeInterval) : from;
      refresh(newFrom, to);
    }, Math.max((10).seconds, pollInterval));

    setBaseTime(currentTimeMillis);

    return () => clearTimeout(interval);
  }, [from, pollInterval]);

  const isLive = !!pollInterval;
  const terminationTime =
    !disableLiveReloading && pollInterval && pollInterval !== 'manual' ? baseTime + pollInterval : 0;

  const [timeRemaining, liveProgress] = (() => {
    if (!terminationTime) {
      return [0, 0];
    }

    const timeProgression = currentTimeMillis - baseTime;
    const fullTimeSpan = terminationTime - baseTime;
    const timeRemaining = terminationTime - currentTimeMillis;

    return [timeRemaining, Math.floor((timeProgression / fullTimeSpan) * 100)];
  })();

  // FIXME: These translations do not relate to the button's behavior.
  const tip = timeRemaining
    ? t(['shared:waitUntilPastLowestInterval'], {
        seconds: Math.max(0, Math.floor(timeRemaining / (1).seconds)),
        defaultValue: '{{seconds}} seconds before you can refresh again',
      })
    : t(['shared:refreshRateIntervalsOf30Seconds'], {
        seconds: lowestLiveInterval / (1).seconds,
        defaultValue: 'Click to refresh. The refresh rate is in intervals of {{seconds}} seconds',
      });

  const tipLoading = t(['shared:loadingData'], {
    defaultValue: 'Loading data',
  });

  const popupState = usePopupState({ variant: 'popover', popupId: 'liveIntervalPicker' });

  useEffect(() => {
    popupState.close();
  }, [pollInterval]);

  const onRefreshClick = useCallback(() => {
    const fromMillis = from.getTime();

    // Bipolar shift (-1 / 1) on each click to force fetching new data.
    const shiftedFrom = fromMillis + (fromMillis % 2) * 2 - 1;

    refresh(new Date(shiftedFrom), to ? new Date(to) : to);
  }, [from, to]);

  const onManualClick = useCallback(() => {
    setPollInterval('manual');
  }, []);

  const onIntervalClick = useCallback(
    ({ target }) => {
      const { intervalIndex = 0 } = target.dataset;

      const interval = pollIntervals[Number(intervalIndex)];

      setPollInterval(interval);
      setBaseTime(currentTimeMillis);
      router.push({ query: { ...router.query, refreshRate: interval } });
    },
    [popupState],
  );

  const menuItems = useMemo(
    () =>
      pollIntervals.map((interval: number, index: number) => {
        const intervalInSeconds = interval / (1).seconds;
        return (
          <MenuItem
            disabled={disableLiveReloading}
            key={interval}
            data-interval-index={index}
            style={{ fontWeight: 300, paddingTop: '5px', paddingBottom: '5px' }}
            onClick={onIntervalClick}
          >
            <Radio
              color="primary"
              disabled={disableLiveReloading}
              className={classes.radio}
              checked={!disableLiveReloading && pollInterval === interval}
              value={`${interval}`}
            />
            {intervalInSeconds > 60
              ? `${intervalInSeconds / 60} ${t(['shared:minutes'], {
                  defaultValue: 'minutes',
                }).toLowerCase()}`
              : `${intervalInSeconds} ${t(['shared:seconds'], {
                  defaultValue: 'seconds',
                }).toLowerCase()}`}
          </MenuItem>
        );
      }),
    [disableLiveReloading, pollInterval, pollIntervals, onIntervalClick],
  );

  return (
    <div className={classes.container} style={overrideStyle}>
      <div className={classes.buttonContainer}>
        <Tooltip title={loading ? tipLoading : tip} placement="top-start">
          <span className={classes.refreshButtonContainer}>
            <Button
              className={classes.button}
              variant={'text'}
              color={isLive ? 'primary' : undefined}
              onClick={onRefreshClick}
              disabled={loading}
            >
              {loading ? <CircularProgress size={16} color={'primary'} thickness={5} /> : <Refresh />}
            </Button>
          </span>
        </Tooltip>
        <Button
          className={classes.dropDownButton}
          variant={'text'}
          color={isLive ? 'primary' : undefined}
          {...bindToggle(popupState)}
        >
          <ArrowDropDown className={classes.dropDownArrow} />
        </Button>
        <Popover
          {...bindPopover(popupState)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper>
            <MenuList dense>
              <MenuItem disabled divider style={{ fontWeight: 500, color: 'black' }}>
                {t(['shared:refreshInterval'], { defaultValue: 'Refresh interval' })}
              </MenuItem>
              <MenuItem style={{ fontWeight: 300, paddingTop: '5px', paddingBottom: '5px' }} onClick={onManualClick}>
                <Radio
                  color="primary"
                  className={classes.radio}
                  checked={disableLiveReloading || !pollInterval || pollInterval === 'manual'}
                  value={'manual'}
                  name={'manual'}
                />
                {t(['shared:manual'], { defaultValue: 'Manual' })}
              </MenuItem>
              {menuItems}
              {disableLiveReloading && (
                <MenuItem
                  key="explanation"
                  style={{
                    fontWeight: 300,
                    maxWidth: '150px',
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    height: 'unset',
                    cursor: 'pointer',
                  }}
                >
                  <Typography variant="caption" style={{ whiteSpace: 'normal' }}>
                    {t(['shared:automaticReloadOnlyIfRelative'], {
                      defaultValue:
                        'Automatic reloading is only available when selecting a relative time range up to {{hours}} hours.',
                      hours: AUTOMATIC_RELOAD_MAX_SPAN_HOURS,
                    })}
                  </Typography>
                </MenuItem>
              )}
            </MenuList>
          </Paper>
        </Popover>
      </div>
      {terminationTime ? (
        <LinearProgress className={classes.progress} variant="determinate" value={100 - liveProgress} />
      ) : null}
    </div>
  );
};

export default LiveDataButton;
